/**
 * Progressive loading for product / article / collection lists.
 *
 * Replaces numbered pagination. The first `data-auto-loads` batches are fetched automatically
 * when the visitor scrolls near the end of the list; after that the visitor clicks the button.
 * That keeps browsing effortless without letting the DOM grow indefinitely, which is what makes
 * pure infinite scroll feel sluggish on long catalogues.
 *
 * The markup is produced by snippets/pagination.liquid. Without JavaScript the button stays a
 * plain link to the next page, so nothing breaks and crawlers can still walk the whole catalogue.
 */
(function () {
  'use strict';

  var STYLE_ID = 'progressive-load-styles';
  var READY_ATTR = 'data-progressive-load-ready';
  var MAX_CHAINED_AUTO_LOADS = 12;

  var CSS = [
    '.ProgressiveLoad{display:flex;flex-direction:column;align-items:center;justify-content:center;',
    'gap:14px;padding:40px 20px 20px;text-align:center;width:100%}',
    '.ProgressiveLoad--tight{padding:24px 20px 8px}',
    '.ProgressiveLoad__Status{margin:0;font-size:12px;letter-spacing:.1em;text-transform:uppercase}',
    '.ProgressiveLoad__Bar{position:relative;width:180px;max-width:60vw;height:2px;border-radius:2px;',
    'background:rgba(128,128,128,.25);overflow:hidden}',
    '.ProgressiveLoad__BarFill{display:block;height:100%;background:currentColor;',
    'transition:width .45s cubic-bezier(.25,.46,.45,.94)}',
    '.ProgressiveLoad__Button{position:relative;min-width:220px}',
    '.ProgressiveLoad__Button.is-loading{pointer-events:none;opacity:.7}',
    '.ProgressiveLoad__Button.is-loading .ProgressiveLoad__ButtonLabel{opacity:.65}',
    '.ProgressiveLoad__Button.is-loading::after{content:"";position:absolute;top:50%;right:16px;',
    'width:13px;height:13px;margin-top:-7px;border:2px solid currentColor;border-right-color:transparent;',
    'border-radius:50%;animation:ProgressiveLoadSpin .7s linear infinite}',
    '.ProgressiveLoad__Spinner{width:22px;height:22px;border:2px solid currentColor;',
    'border-right-color:transparent;border-radius:50%;opacity:.6;',
    'animation:ProgressiveLoadSpin .7s linear infinite}',
    '.ProgressiveLoad.is-autoloading .ProgressiveLoad__Button{display:none}',
    '.ProgressiveLoad:not(.is-autoloading) .ProgressiveLoad__Spinner{display:none}',
    '.ProgressiveLoad.is-done .ProgressiveLoad__Bar,',
    '.ProgressiveLoad.is-done .ProgressiveLoad__Button{display:none}',
    '@keyframes ProgressiveLoadSpin{to{transform:rotate(360deg)}}',
    '@media (prefers-reduced-motion:reduce){.ProgressiveLoad__BarFill{transition:none}',
    '.ProgressiveLoad__Button.is-loading::after,.ProgressiveLoad__Spinner{animation-duration:2s}}'
  ].join('');

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.appendChild(document.createTextNode(CSS));
    (document.head || document.documentElement).appendChild(style);
  }

  function toInt(value, fallback) {
    var parsed = parseInt(value, 10);
    return isNaN(parsed) ? fallback : parsed;
  }

  function matches(element, selector) {
    if (!element || element.nodeType !== 1) return false;
    var fn = element.matches || element.msMatchesSelector || element.webkitMatchesSelector;
    return fn ? fn.call(element, selector) : false;
  }

  function splitSelector(selector) {
    var parts = String(selector).split(',');
    var cleaned = [];

    for (var i = 0; i < parts.length; i++) {
      var part = parts[i].replace(/^\s+|\s+$/g, '');
      if (part) cleaned.push(part);
    }

    return cleaned.length ? cleaned : ['.ProductList'];
  }

  function Loader(root) {
    this.root = root;
    this.listSelector = root.getAttribute('data-list-selector') || '.ProductList';
    this.selectors = splitSelector(this.listSelector);
    this.matchedSelector = this.selectors[0];
    this.nextUrl = root.getAttribute('data-next-url') || '';
    this.autoLeft = toInt(root.getAttribute('data-auto-loads'), 2);
    this.loaded = toInt(root.getAttribute('data-loaded'), 0);
    this.total = toInt(root.getAttribute('data-total'), 0);

    this.labels = {
      more: root.getAttribute('data-label-more') || 'Load more',
      loading: root.getAttribute('data-label-loading') || 'Loading …',
      count: root.getAttribute('data-label-count') || '[loaded] / [total]',
      end: root.getAttribute('data-label-end') || '',
      error: root.getAttribute('data-label-error') || 'Loading failed – please try again'
    };

    this.button = root.querySelector('[data-progressive-load-button]');
    this.label = root.querySelector('[data-progressive-load-label]');
    this.status = root.querySelector('[data-progressive-load-status]');
    this.bar = root.querySelector('[data-progressive-load-bar]');

    this.busy = false;
    this.chainedAutoLoads = 0;
    this.list = this.findList();

    this.addSpinner();
    this.bindButton();
    this.observe();
  }

  /**
   * Finds the list that receives the new items: the closest element matching the selector that
   * sits before this loader in the document.
   */
  Loader.prototype.findList = function () {
    var selector = this.listSelector;
    var node = this.root;

    while (node && node !== document.body) {
      var sibling = node.previousElementSibling;

      while (sibling) {
        if (matches(sibling, selector)) return this.remember(sibling);
        var nested = sibling.querySelector(selector);
        if (nested) return this.remember(nested);
        sibling = sibling.previousElementSibling;
      }

      node = node.parentElement;
    }

    var all = document.querySelectorAll(selector);
    return all.length ? this.remember(all[all.length - 1]) : null;
  };

  /**
   * Notes which of the candidate selectors actually matched, so the same one can be used to pick
   * the matching list out of the fetched page.
   */
  Loader.prototype.remember = function (element) {
    for (var i = 0; i < this.selectors.length; i++) {
      if (matches(element, this.selectors[i])) {
        this.matchedSelector = this.selectors[i];
        break;
      }
    }

    return element;
  };

  Loader.prototype.addSpinner = function () {
    if (this.root.querySelector('.ProgressiveLoad__Spinner')) return;
    var spinner = document.createElement('span');
    spinner.className = 'ProgressiveLoad__Spinner';
    spinner.setAttribute('aria-hidden', 'true');
    this.root.appendChild(spinner);
  };

  Loader.prototype.bindButton = function () {
    if (!this.button) return;
    var self = this;

    // The href is only there as a no-JS fallback. With JS running it has to go, otherwise the
    // theme's page-transition handler would navigate away instead of letting us load in place.
    this.button.removeAttribute('href');
    this.button.setAttribute('role', 'button');
    this.button.setAttribute('tabindex', '0');

    function trigger(event) {
      event.preventDefault();
      event.stopPropagation();
      self.autoLeft = 0;
      self.chainedAutoLoads = 0;
      self.load();
    }

    this.button.addEventListener('click', trigger);

    this.button.addEventListener('keydown', function (event) {
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') trigger(event);
    });
  };

  Loader.prototype.observe = function () {
    if (typeof IntersectionObserver === 'undefined') return;
    var self = this;

    this.observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) self.autoLoad();
        }
      },
      { rootMargin: '700px 0px' }
    );

    this.observer.observe(this.root);
  };

  Loader.prototype.autoLoad = function () {
    if (this.autoLeft <= 0 || !this.nextUrl || this.busy) return;
    if (this.chainedAutoLoads >= MAX_CHAINED_AUTO_LOADS) return;
    this.autoLeft -= 1;
    this.chainedAutoLoads += 1;
    this.root.classList.add('is-autoloading');
    this.load();
  };

  Loader.prototype.setBusy = function (busy) {
    this.busy = busy;

    if (this.button) {
      this.button.classList.toggle('is-loading', busy);
      this.button.setAttribute('aria-busy', busy ? 'true' : 'false');
    }

    if (this.label) {
      this.label.textContent = busy ? this.labels.loading : this.labels.more;
    }
  };

  Loader.prototype.render = function () {
    var done = !this.nextUrl;

    if (this.status) {
      this.status.textContent = done
        ? this.labels.end.replace('[total]', this.total)
        : this.labels.count.replace('[loaded]', this.loaded).replace('[total]', this.total);
    }

    if (this.bar && this.total > 0) {
      this.bar.style.width = Math.min(100, (this.loaded / this.total) * 100) + '%';
    }

    if (done) {
      this.root.classList.add('is-done');
      this.root.classList.remove('is-autoloading');
      if (this.button && this.button.parentNode) this.button.parentNode.removeChild(this.button);
      if (this.observer) this.observer.disconnect();
    }

    this.root.setAttribute('data-next-url', this.nextUrl);
    this.root.setAttribute('data-loaded', this.loaded);
  };

  Loader.prototype.load = function () {
    if (this.busy || !this.nextUrl) return;

    var self = this;
    var url = this.nextUrl;

    this.setBusy(true);

    fetch(url, { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Request failed with status ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var appended = self.appendItems(doc);
        var nextLoader = doc.querySelector('[data-progressive-load]');

        if (nextLoader && nextLoader.getAttribute('data-next-url')) {
          self.nextUrl = nextLoader.getAttribute('data-next-url');
          self.loaded = toInt(nextLoader.getAttribute('data-loaded'), self.loaded + appended);
          self.total = toInt(nextLoader.getAttribute('data-total'), self.total);
        } else {
          self.nextUrl = '';
          self.loaded = self.total || self.loaded + appended;
        }

        self.setBusy(false);
        self.root.classList.remove('is-autoloading', 'has-error');
        self.render();
        self.notify(appended);

        // The loader may still sit inside the viewport after appending, in which case the
        // IntersectionObserver will not fire again on its own.
        window.requestAnimationFrame(function () {
          if (self.autoLeft > 0 && self.nextUrl && self.isNearViewport()) self.autoLoad();
          else self.chainedAutoLoads = 0;
        });
      })
      .catch(function (error) {
        if (window.console && window.console.warn) {
          window.console.warn('[progressive-load] ' + error.message);
        }
        self.setBusy(false);
        self.autoLeft = 0;
        self.root.classList.remove('is-autoloading');
        self.root.classList.add('has-error');
        if (self.status) self.status.textContent = self.labels.error;
      });
  };

  Loader.prototype.appendItems = function (doc) {
    if (!this.list) this.list = this.findList();
    if (!this.list) return 0;

    var source = doc.querySelector(this.matchedSelector) || doc.querySelector(this.listSelector);
    if (!source) return 0;

    var fragment = document.createDocumentFragment();
    var count = 0;

    while (source.firstElementChild) {
      fragment.appendChild(source.firstElementChild);
      count += 1;
    }

    this.list.appendChild(fragment);
    return count;
  };

  Loader.prototype.isNearViewport = function () {
    var rect = this.root.getBoundingClientRect();
    return rect.top < (window.innerHeight || 0) + 700;
  };

  /** Lets lazy loading and other theme scripts pick up the freshly inserted markup. */
  Loader.prototype.notify = function (appended) {
    if (window.lazySizes && window.lazySizes.loader && window.lazySizes.loader.checkElems) {
      window.lazySizes.loader.checkElems();
    }

    var detail = { container: this.list, appended: appended, loader: this.root };

    document.dispatchEvent(new CustomEvent('progressive-load:appended', { detail: detail, bubbles: true }));
  };

  function init() {
    injectStyles();

    var roots = document.querySelectorAll('[data-progressive-load]:not([' + READY_ATTR + '])');

    for (var i = 0; i < roots.length; i++) {
      roots[i].setAttribute(READY_ATTR, 'true');
      new Loader(roots[i]);
    }
  }

  function watch() {
    if (typeof MutationObserver === 'undefined') return;

    var scheduled = false;

    new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(function () {
        scheduled = false;
        init();
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  function boot() {
    init();
    watch();
    document.addEventListener('shopify:section:load', init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
